import React, { useState, useEffect, useCallback } from 'react';
import { SxProps, Theme, Typography } from '@mui/material';
import { LabeledSelect } from 'components/views/atoms/LabeledSelect';
import { LabeledNumberField } from 'components/views/atoms/LabeledNumberField';
import { MediaDevice, CameraDevice } from 'core/models/mediaDevice';
import { cancellable } from 'services/utility/cancellable';
import { SyncButton } from 'components/views/atoms/SyncButton';
import { SafeBox } from 'components/views/atoms/SafeBox';

const resolutions: MediaDevice.VideoResolution[] = [
    'QVGA',
    'VGA',
    'HD',
    'FHD',
];

const scale = 1000;

function applyScale(v: number | undefined) {
    return (v === undefined) ? undefined : Math.floor(v / scale);
}

function equalConfig(a: Partial<MediaDevice.VideoConfig>, b: Partial<MediaDevice.VideoConfig>) {
    if (a.enabled !== b.enabled) return false;
    if (applyScale(a.maxBitrate) !== applyScale(b.maxBitrate)) return false;
    if (applyScale(a.minBitrate) !== applyScale(b.minBitrate)) return false;
    if (a.framerate !== b.framerate) return false;
    if (a.resolution != b.resolution) return false;
    return true;
}

const styles = {
    formControl: {
        marginLeft: theme => theme.spacing(0.5),
        marginRight: theme => theme.spacing(0.5),
    } as SxProps<Theme>,
    parameters: {
        marginLeft: '1em',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    } as SxProps<Theme>,
    bitrates: {
        display: 'flex',
        flexDirection: 'row',
    } as SxProps<Theme>
};

export type VideoStreamConfigFormProps = {
    camera?: CameraDevice;
    disabled?: boolean;
    getVideoConfig?: (mediaDeviceId: MediaDevice.Id) => Promise<Partial<MediaDevice.VideoConfig>>;
    putVideoConfig?: (mediaDeviceId: MediaDevice.Id, config: Partial<MediaDevice.VideoConfig>) => Promise<void>;
}

export function VideoStreamConfigForm(props: VideoStreamConfigFormProps) {
    const { camera, disabled = false, getVideoConfig, putVideoConfig } = props;

    const [remoteConfig, setRemoteConfig] = useState<Partial<MediaDevice.AudioConfig>>();
    const [config, setConfig] = useState<Partial<MediaDevice.VideoConfig>>({});
    const [dirty, setDirty] = useState(false);
    const [syncing, setSyncing] = useState(false);

    useEffect(() => {
        setConfig({});
        setDirty(true);
    }, [camera]);

    useEffect(() => {
        if (!getVideoConfig || !camera) return;
        if (!dirty) return;

        setSyncing(true);

        return cancellable(async isCancel => {
            const config = await getVideoConfig(camera.id);
            if (isCancel()) return;
            setRemoteConfig(config);
            setConfig(config);
            setSyncing(false);
            setDirty(false);
        });
    }, [getVideoConfig, camera, dirty]);

    const [modified, setModified] = useState(false);

    const modify = useCallback((config: Partial<MediaDevice.VideoConfig>) => {
        setConfig(prev => {
            const updated = { ...prev, ...config };
            if (remoteConfig && equalConfig(remoteConfig, updated)) {
                setModified(false);
                return remoteConfig;
            } else {
                setModified(true);
                return updated;
            }
        });
    }, [remoteConfig]);

    const update = useCallback(() => {
        console.log({ update, putConfig: putVideoConfig, camera, config });
        if (!putVideoConfig || !camera || !config) return;

        setSyncing(true);

        return cancellable(async isCancel => {
            await putVideoConfig(camera.id, config);
            if (isCancel()) return;
            setModified(false);
            setDirty(true);
        });
    }, [putVideoConfig, camera, config]);

    const { formControl, parameters, bitrates } = styles;

    return (
        <SafeBox sx={formControl}>
            <SafeBox>
                <Typography variant='body1'>
                    <span>video settings </span>
                    <SyncButton
                        syncing={syncing}
                        hidden={!syncing && !modified}
                        disabled={putVideoConfig === undefined}
                        onClick={update} />
                </Typography>
            </SafeBox>
            <SafeBox sx={parameters}>
                <LabeledSelect
                    label='resolution'
                    disabled={disabled || syncing}
                    options={resolutions}
                    selected={config?.resolution}
                    onChange={resolution => modify({ resolution })}
                />
                <SafeBox sx={bitrates}>
                    <LabeledNumberField
                        label='min bitrate'
                        disabled={disabled || syncing}
                        value={config?.minBitrate}
                        scale={scale}
                        suffix='kbps'
                        onChange={minBitrate => modify({ minBitrate })}
                    />
                    <LabeledNumberField
                        label='max bitrate'
                        disabled={disabled || syncing}
                        value={config?.maxBitrate}
                        scale={scale}
                        suffix='kbps'
                        onChange={maxBitrate => modify({ maxBitrate })}
                    />
                </SafeBox>
                <LabeledNumberField
                    label='framerate'
                    disabled={disabled || syncing}
                    value={config?.framerate}
                    suffix='fps'
                    onChange={framerate => modify({ framerate })}
                />
            </SafeBox>
        </SafeBox>
    );
}
